import { useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { GRAY, GRAY_SECONDARY, PRIMARY } from 'constants/colors';

import Button from '@mui/material/Button';

import { Dialog } from 'components/shared';

import EmbeddedDashboardBuyDialog from './EmbeddedDashboardBuyDialog';
import { Icon } from '@sdb/planning-ui';

const StyledPlanCard = styled.div`
  border: 1px solid;
  border-radius: 2px;

  &.has-action {
    padding-bottom: 36px;
    position: relative;

    .action-btn {
      position: absolute;
      left: 0px;
      right: 0px;
      bottom: 0px;
      width: 100%;
    }
  }

  .card-header {
    padding: 10px 20px;
  }

  &.DEFAULT {
    border-color: ${GRAY};

    .card-header {
      background-color: ${GRAY};
    }
  }

  &.PRIMARY {
    border-color: ${PRIMARY};

    .card-header {
      background-color: ${PRIMARY};

      h2,
      p {
        color: white;
      }
    }
  }

  .card-body {
    padding: 20px 20px 40px;

    .body-description {
      margin-bottom: 20px;
    }

    .feature-entry {
      margin-bottom: 10px;

      .feature-title {
        display: flex;
        align-items: center;
      }

      svg {
        margin-right: 5px;
      }

      .feature-description {
        color: ${GRAY_SECONDARY};
      }
    }
  }
`;

type PlanCardProps = {
  variant?: 'DEFAULT' | 'PRIMARY';
  title: string;
  subtitle: string;
  infoText: string;
  features: {
    title: string;
    description: string;
  }[];
  footerInfo?: string;
  action?: {
    text: string;
    callback: () => void;
  };
};

const PlanCard = ({
  variant = 'DEFAULT',
  title,
  subtitle,
  infoText,
  features,
  action,
  footerInfo
}: PlanCardProps) => (
  <StyledPlanCard
    className={classNames(
      'plan-card',
      { 'has-action': Boolean(action) },
      variant
    )}
  >
    <div className="card-header">
      <h2>{title}</h2>
      <p>{subtitle}</p>
    </div>
    <div className="card-body">
      <p className="body-description">{infoText}</p>
      {features.map(({ title, description }) => (
        <div className="feature-entry" key={title}>
          <div className="feature-title">
            <Icon type="check" fontSize="small" color="primary" />
            <p>{title}</p>
          </div>
          <p className="feature-description">{description}</p>
        </div>
      ))}
      {footerInfo && <h4>{footerInfo}</h4>}
    </div>
    {action && (
      <Button
        onClick={action.callback}
        color="primary"
        variant="contained"
        className="action-btn"
      >
        {action.text}
      </Button>
    )}
  </StyledPlanCard>
);

const StyledContent = styled.div`
  h2,
  p {
    margin: 0;
  }
  .info-text {
    margin-bottom: 20px;

    p {
      margin-bottom: 15px;
    }
  }

  .cards {
    display: flex;

    .plan-card {
      margin: 0 5px;

      &:first-child {
        margin-left: 0;
      }
      &:last-child {
        margin-right: 0;
      }
    }
  }
`;

type Props = {
  isTrialActive: boolean;
  numberOfTrialDaysLeft: number;
  onHidden: () => void;
  onBuyAdvanced: () => void;
};

export default function EmbeddedDashboardComparisonDialog({
  isTrialActive,
  numberOfTrialDaysLeft,
  onHidden,
  onBuyAdvanced
}: Props) {
  const { t } = useTranslation('common');
  const [showBuyDialog, setShowBuyDialog] = useState(false);
  const [hide, setHide] = useState(false);

  const onBuyAdvancedRequestSent = () => {
    setHide(true);
    onBuyAdvanced();
  };

  return (
    <Dialog
      title={t('embeddedDashboard.buy.title')}
      open={!hide}
      onHidden={onHidden}
      headerBgColor={PRIMARY}
    >
      <StyledContent>
        <div className="info-text">
          <p>
            {isTrialActive
              ? t('embeddedDashboard.buy.paragraph1b_interval', {
                  postProcess: 'interval',
                  count: numberOfTrialDaysLeft
                })
              : t('embeddedDashboard.buy.paragraph1a')}
          </p>
          <p>{t('embeddedDashboard.buy.paragraph2')}</p>
          <p>{t('embeddedDashboard.buy.paragraph3')}</p>
        </div>

        <div className="cards">
          <PlanCard
            title={t('embeddedDashboard.buy.basic.title')}
            subtitle={t('embeddedDashboard.buy.basic.subtitle')}
            infoText={t('embeddedDashboard.buy.basic.description')}
            features={[
              {
                title: t('embeddedDashboard.buy.feature1.title'),
                description: t('embeddedDashboard.buy.feature1.description')
              },
              {
                title: t('embeddedDashboard.buy.feature2.title'),
                description: t('embeddedDashboard.buy.feature2.description')
              },
              {
                title: t('embeddedDashboard.buy.feature3.title'),
                description: t('embeddedDashboard.buy.feature3.description')
              }
            ]}
          />
          <PlanCard
            variant="PRIMARY"
            title={t('embeddedDashboard.buy.advanced.title')}
            subtitle={t('embeddedDashboard.buy.advanced.subtitle')}
            infoText={t('embeddedDashboard.buy.advanced.description')}
            features={[
              {
                title: t('embeddedDashboard.buy.feature1.title'),
                description: t('embeddedDashboard.buy.feature1.description')
              },
              {
                title: t('embeddedDashboard.buy.feature2.title'),
                description: t('embeddedDashboard.buy.feature2.description')
              },
              {
                title: t('embeddedDashboard.buy.feature3.title'),
                description: t('embeddedDashboard.buy.feature3.description')
              },
              {
                title: t('embeddedDashboard.buy.feature4.title'),
                description: t('embeddedDashboard.buy.feature4.description')
              },
              {
                title: t('embeddedDashboard.buy.feature5.title'),
                description: t('embeddedDashboard.buy.feature5.description')
              },
              {
                title: t('embeddedDashboard.buy.feature6.title'),
                description: t('embeddedDashboard.buy.feature6.description')
              }
            ]}
            footerInfo={t('embeddedDashboard.buy.price')}
            action={{
              text: t('embeddedDashboard.buy.applyNow'),
              callback: () => setShowBuyDialog(true)
            }}
          />
        </div>
      </StyledContent>
      {showBuyDialog && (
        <EmbeddedDashboardBuyDialog
          onHidden={() => setShowBuyDialog(false)}
          onRequestSent={onBuyAdvancedRequestSent}
        />
      )}
    </Dialog>
  );
}
